import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, requestIdleCallback, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    onDocumentLoaded(this.#armWarm);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.addEventListener('pointermove', this.#onLatePointer, { passive: true });
    this.addEventListener('pointerdown', this.#onPointerDown, { passive: true });
    this.addEventListener('click', this.#onTap);
    document.addEventListener('pointerdown', this.#onOutsidePointerDown, { passive: true });
    requestAnimationFrame(this.#openHovered);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('pointermove', this.#onFirstMouseMove);
    this.removeEventListener('pointermove', this.#onLatePointer);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('click', this.#onTap);
    document.removeEventListener('pointerdown', this.#onOutsidePointerDown);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    // MYNSU storefront v2 (10/24/2026): a finger or pen "hovers" the item it taps and "leaves" it right after lifting,
    // so on touch screens the panel would flash open and shut while the tap follows the link. Taps are handled in #onTap.
    if (isTouchPointerEvent(event, 'pointerenter')) return;

    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item) return;

    // MYNSU storefront v2 (10/24/2026; Aden: hovering Rings sometimes did nothing at all): the active item returns early only
    // while its panel is really open; if anything closed the panel without going through #deactivate, open it again.
    if (item == this.#state.activeItem && (item.ariaExpanded === 'true' && (!findSubmenu(item) || this.#panelShowing()))) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';
      // MYNSU storefront v2 (10/24/2026): start this panel's tile photos now, not when the lazy loader gets to them
      submenu.querySelectorAll('.mm2 img[loading="lazy"]').forEach((image) => image.removeAttribute('loading'));
      if (document.readyState === 'complete') this.#warm();

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    // MYNSU storefront v2 (10/24/2026): a panel measured 0px tall would open invisibly; measure it again on the next frame
    if (hasSubmenu && isDefaultSlot && finalHeight === 0) {
      requestAnimationFrame(() => {
        const height = submenu?.offsetHeight || 0;
        if (this.#state.activeItem !== item || height === 0) return;
        this.headerComponent?.style.setProperty('--submenu-height', `${height}px`);
        this.#setFullOpenHeaderHeight(height);
      });
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    // MYNSU storefront v2 (10/24/2026): a finger leaves the item as soon as it lifts; a panel opened by a tap stays open (#onTap)
    if (isTouchPointerEvent(event, 'pointerleave')) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    // MYNSU storefront v2 (10/23/2026): the icons and tiles of the v2 dropdown (snippets/mega-menu-v2.liquid) are left lazy,
    // so they download when a menu opens and never on phones, where this menu is not shown.
    const images = this.querySelectorAll('img[loading="lazy"]:not(.mm2 img)');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /*
   * MYNSU storefront v2 (10/24/2026; Aden: the dropdown must open as instantly as the one on jeulia.com).
   * The v2 dropdown's icons (stylesheet backgrounds) and tile photos (lazy images) used to download only when a menu
   * opened, so a first hover showed empty icons and grey tiles. On a device that shows this menu, the first real mouse
   * movement after the page has loaded now fetches them for every menu while the browser is idle: about 24 small SVG
   * files and the tile photos, once, then from the cache. Phones never show this menu, and phones and page-speed tests
   * never move a mouse, so nothing changes for them and the page load itself is the same.
   */
  #warmed = false;

  #armWarm = () => {
    if (!this.#warmed) document.addEventListener('pointermove', this.#onFirstMouseMove, { passive: true });
  };

  /** @param {PointerEvent} event */
  #onFirstMouseMove = (event) => {
    if (event.pointerType !== 'mouse') return;
    document.removeEventListener('pointermove', this.#onFirstMouseMove);
    requestIdleCallback(this.#warm, { timeout: 800 });
  };

  #warm = () => {
    // The desktop menu is display: none on phones; nothing to fetch there
    if (this.#warmed || !this.checkVisibility()) return;
    this.#warmed = true;
    document.documentElement.classList.add('mm2-warm');
    this.querySelectorAll('.mm2 img[loading="lazy"]').forEach((image) => image.removeAttribute('loading'));
    // Closed submenus are content-visibility: auto, so the browser skips their styles and never fetches the icon
    // backgrounds; fetch each icon file directly (same address, so the open panel paints it from the cache)
    const icons = new Set();
    this.querySelectorAll('.mm2-ic').forEach((icon) => {
      const url = getComputedStyle(icon).backgroundImage.match(/url\("?([^")]+)"?\)/)?.[1];
      if (url) icons.add(url);
    });
    icons.forEach((url) => {
      new Image().src = url;
    });
  };

  /**
   * MYNSU storefront v2 (10/24/2026): a pointer that entered a menu item before this element was defined (the first
   * moments of a page load; about 3 seconds in a real Chrome on the preview host) never produced a pointerenter here, so
   * the menu stayed shut until the pointer left the item and came back. Two catch-ups, the same dispatch Horizon's own
   * #reconcilePointerTarget uses: when this element is defined, a pointer already resting on an item opens it; until the
   * first menu has opened, any mouse movement inside a closed item opens it. After that pointerenter does the job.
   */
  #openHovered = () => {
    if (this.#state.activeItem || !window.matchMedia('(hover: hover)').matches) return;
    const item = this.querySelector('.menu-list__list-item:hover');
    if (item) item.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
  };

  /** @param {PointerEvent} event */
  #onLatePointer = (event) => {
    if (event.pointerType !== 'mouse') return;
    if (this.#state.activeItem) {
      this.removeEventListener('pointermove', this.#onLatePointer);
      return;
    }
    const item = event.target instanceof Element ? event.target.closest('.menu-list__list-item') : null;
    if (item && this.contains(item)) item.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
  };

  /**
   * MYNSU storefront v2 (10/24/2026): whether the open item's panel can be seen. Its height lives on header-component,
   * and section hydration takes data-active off the submenu, so both are checked.
   * @returns {boolean}
   */
  #panelShowing() {
    const submenu = findSubmenu(this.#state.activeItem);
    const height = parseFloat(this.headerComponent?.style.getPropertyValue('--full-open-header-height') || '0');
    return height > 0 && (!submenu || submenu.hasAttribute('data-active'));
  }

  /*
   * MYNSU storefront v2 (10/24/2026; tablets): an iPad or an Android tablet shows this desktop menu but has no hover, so
   * tapping Rings followed the link and its dropdown could never be seen. With a finger or a pen, the first tap on an item
   * that has a dropdown opens the dropdown, a second tap on it follows the link, and a tap outside the menu closes it.
   * Mouse and keyboard work as before.
   */
  #tap = { pointerType: '', wasOpen: false };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    const li = this.#tapTarget(event.target);
    const item = li ? findMenuItem(li) : null;
    this.#tap = {
      pointerType: event.pointerType,
      wasOpen: Boolean(item && item === this.#state.activeItem && item.ariaExpanded === 'true' && this.#panelShowing()),
    };
  };

  /** @param {MouseEvent} event */
  #onTap = (event) => {
    // The type seen at pointerdown wins: WebKit reports the click that follows a finger tap as pointerType "mouse"
    const pointerType = this.#tap.pointerType || /** @type {PointerEvent} */ (event).pointerType;
    const { wasOpen } = this.#tap;
    this.#tap = { pointerType: '', wasOpen: false };
    // event.detail is 0 for a click from the keyboard
    if (event.detail === 0 || (pointerType !== 'touch' && pointerType !== 'pen')) return;
    const li = this.#tapTarget(event.target);
    if (!li || wasOpen) return;
    event.preventDefault();
    this.activate(/** @type {PointerEvent} */ (/** @type {unknown} */ ({ target: li })));
  };

  /** @param {PointerEvent} event */
  #onOutsidePointerDown = (event) => {
    if (!this.#state.activeItem || event.pointerType === 'mouse' || !event.pointerType) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#deactivate();
  };

  /**
   * The top-level menu item whose link was tapped, when that item opens a dropdown (or is the "More" item).
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  #tapTarget(target) {
    if (!(target instanceof Element)) return null;
    const li = target.closest('.menu-list__link')?.closest('.menu-list__list-item');
    if (!(li instanceof HTMLElement) || !this.contains(li)) return null;
    return li.slot === 'more' || findSubmenu(findMenuItem(li)) ? li : null;
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * MYNSU storefront v2 (10/24/2026): whether `event` is a `type` pointer event from a finger or a pen. The pointerenter
 * events this file dispatches itself have no pointer type and still count as hovers.
 * @param {Event} event
 * @param {string} type
 * @returns {boolean}
 */
function isTouchPointerEvent(event, type) {
  return (
    event.type === type && event instanceof PointerEvent && (event.pointerType === 'touch' || event.pointerType === 'pen')
  );
}
